import { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classnames from 'clsx';

import Global from '../../../../store/global-selectors';
import { useEnableShowIndividualItemsOnOrderSidebar } from '../../../../feature-flags/hooks/use-enable-show-individual-items-on-order-sidebar';

import SidebarItemList from './item-list';
import messages from './messages';
import style from './grouped-order-items.module.less';

const buildGroupings = (fulfillmentPlans, shouldShowIndividualItems) => {
  const isSingleOrder = fulfillmentPlans.length === 1;
  const getGroupingKey = (item) => {
    const fallbackKey = item.sku || item.name;
    if (shouldShowIndividualItems && isSingleOrder) {
      return item.fulfillmentPlanItemId || fallbackKey;
    }
    return fallbackKey;
  };
  const allItems = fulfillmentPlans.reduce((combinedItems, fp) => {
    const { items } = fp;
    return [...combinedItems, ...items];
  }, []);
  const groupings = allItems.reduce((grouped, item) => {
    const { quantity } = item;
    const groupingKey = getGroupingKey(item);
    const currentCount = grouped[groupingKey] ? grouped[groupingKey].quantity : 0;
    return {
      ...grouped,
      [groupingKey]: {
        ...item,
        quantity: currentCount + quantity,
      },
    };
  }, {});
  return Object.keys(groupings)
    .map((key) => groupings[key])
    .sort((a, b) => b.quantity - a.quantity);
};

/**
 * @param {object} props
 * @param {string[]} props.fulfillmentPlanIds
 * @param {string} [props.itemsListContainerClassName]
 * @param {(productId: string) => void} props.openProductDetailModal
 * @returns {JSX.Element}
 */
const GroupByItem = ({ fulfillmentPlanIds, itemsListContainerClassName, openProductDetailModal }) => {
  const fulfillmentPlans = useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }));
  const shouldShowIndividualItems = useEnableShowIndividualItemsOnOrderSidebar();
  const groupedItems = useMemo(
    () => buildGroupings(fulfillmentPlans, shouldShowIndividualItems),
    [fulfillmentPlans, shouldShowIndividualItems],
  );
  const itemListClassnames = classnames(style['item-list'], style['without-inventory']);

  return (
    <div className={classnames(style['item-list-wrapper'], itemsListContainerClassName)}>
      <div className={itemListClassnames}>
        {groupedItems.length ? (
          <SidebarItemList fulfillmentPlanId='item-grouping' items={groupedItems} openProductDetailModal={openProductDetailModal}>
            <div className={classnames(style['item-list-header'], style['item-list-title'])}>
              <FormattedMessage {...messages.itemsTitle} />
            </div>
          </SidebarItemList>
        ) : (
          <FormattedMessage {...messages.noItems} />
        )}
      </div>
    </div>
  );
};

export default GroupByItem;
